import { useState } from 'react'
import { AutomationRule, AutomationRuleFormData, TriggerType, ConditionOperator } from '../types/automationRule'
import './AutomationRuleForm.css'

interface AutomationRuleFormProps {
  initialData?: AutomationRule
  onSubmit: (data: AutomationRuleFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export function AutomationRuleForm({ initialData, onSubmit, onCancel, isLoading }: AutomationRuleFormProps) {
  const [formData, setFormData] = useState<AutomationRuleFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    triggerType: initialData?.triggerType || 'lab-result-created',
    enabled: initialData?.enabled ?? true,
    condition: initialData?.condition || {
      field: '',
      operator: 'equals',
      value: '',
    },
    action: initialData?.action || {
      type: 'create-task',
      title: '',
      description: '',
      priority: 'medium',
      urgency: 'routine',
      ehrComponentType: '',
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const triggerTypes: TriggerType[] = [
    'lab-result-created',
    'lab-result-updated',
    'medication-review-due',
    'appointment-scheduled',
    'consultation-completed',
    'vital-sign-abnormal',
  ]

  const operators: ConditionOperator[] = ['equals', 'notEquals', 'contains', 'lessThanOrEqual', 'greaterThanOrEqual']

  const priorities: Array<'low' | 'medium' | 'high' | 'urgent'> = ['low', 'medium', 'high', 'urgent']
  const urgencies: Array<'routine' | 'urgent' | 'stat' | 'asap'> = ['routine', 'urgent', 'stat', 'asap']

  return (
    <form className="automation-rule-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name">
          Rule Name <span className="required">*</span>
        </label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="e.g., Critical Lab Value Alert"
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe what this rule does"
          rows={3}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="triggerType">
            Trigger Type <span className="required">*</span>
          </label>
          <select
            id="triggerType"
            value={formData.triggerType}
            onChange={(e) => setFormData({ ...formData, triggerType: e.target.value as TriggerType })}
            required
          >
            {triggerTypes.map((type) => (
              <option key={type} value={type}>
                {type.replace(/-/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>
      </div>

      <div className="form-section">
        <h3>Condition</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="conditionField">
              Field <span className="required">*</span>
            </label>
            <input
              id="conditionField"
              type="text"
              value={formData.condition.field}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  condition: { ...formData.condition, field: e.target.value },
                })
              }
              required
              placeholder="e.g., reviewStatus, isCritical"
            />
          </div>

          <div className="form-group">
            <label htmlFor="conditionOperator">
              Operator <span className="required">*</span>
            </label>
            <select
              id="conditionOperator"
              value={formData.condition.operator}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  condition: { ...formData.condition, operator: e.target.value as ConditionOperator },
                })
              }
              required
            >
              {operators.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="conditionValue">
              Value <span className="required">*</span>
            </label>
            <input
              id="conditionValue"
              type="text"
              value={formData.condition.value}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  condition: { ...formData.condition, value: e.target.value },
                })
              }
              required
              placeholder="e.g., in-review, true, now"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Action</h3>
        <div className="form-group">
          <label htmlFor="actionTitle">
            Task Title <span className="required">*</span>
          </label>
          <input
            id="actionTitle"
            type="text"
            value={formData.action.title}
            onChange={(e) =>
              setFormData({
                ...formData,
                action: { ...formData.action, title: e.target.value },
              })
            }
            required
            placeholder="e.g., Follow up on lab result"
          />
        </div>

        <div className="form-group">
          <label htmlFor="actionDescription">Task Description</label>
          <textarea
            id="actionDescription"
            value={formData.action.description || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                action: { ...formData.action, description: e.target.value },
              })
            }
            placeholder="Description for the created task"
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="actionPriority">
              Priority <span className="required">*</span>
            </label>
            <select
              id="actionPriority"
              value={formData.action.priority}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  action: { ...formData.action, priority: e.target.value as any },
                })
              }
              required
            >
              {priorities.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="actionUrgency">Urgency</label>
            <select
              id="actionUrgency"
              value={formData.action.urgency || 'routine'}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  action: { ...formData.action, urgency: e.target.value as any },
                })
              }
            >
              {urgencies.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ehrComponentType">EHR Component Type</label>
            <input
              id="ehrComponentType"
              type="text"
              value={formData.action.ehrComponentType || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  action: { ...formData.action, ehrComponentType: e.target.value },
                })
              }
              placeholder="e.g., lab-result, medication"
            />
          </div>

          <div className="form-group">
            <label htmlFor="dueDateOffset">Due Date Offset (days)</label>
            <input
              id="dueDateOffset"
              type="number"
              value={formData.action.dueDateOffset || 0}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  action: { ...formData.action, dueDateOffset: parseInt(e.target.value) || 0 },
                })
              }
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update Rule' : 'Create Rule'}
        </button>
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancel
        </button>
      </div>
    </form>
  )
}

